import {
  Box,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import type { FC } from 'react';
import React, { memo, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { fetchProperty } from '../components/data/SWR';
import useGaTracker from '../components/PageTracking';
import useSeo from '../components/useSeo';
import { strapiUrl } from '../config/env';

interface ParamsProps {
  property: string;
}

const Ejendom: FC = memo(() => {
  const params = useParams<ParamsProps>();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, isLoading, isError } = fetchProperty(Number(params.property));

  useGaTracker();
  useSeo({
    title: data?.Adresse
      ? `${data.Adresse} | Ejendom i Silkeborg | Toft Ejendomme`
      : 'Ejendom i Silkeborg | Toft Ejendomme',
    description:
      data?.Beskrivelsse?.slice(0, 160) ||
      'Se detaljer, billeder og information om en af vores ejendomme i Silkeborg.',
    path: `/ejendom/${params.property}`,
    ogType: 'article',
    noIndex: Boolean(isError),
  });

  const galleryImages = useMemo(() => {
    return [...(data?.Ekstra_Billeder || []), ...(data?.Galleri || [])];
  }, [data]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
      }

      if (event.key === 'ArrowLeft') {
        setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [galleryImages.length, isOpen]);

  if (isLoading) {
    return (
      <section className="editorial-section">
        <div className="section-wrap">
          <Skeleton borderRadius="22px" height="60vh" />
        </div>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section className="editorial-section">
        <div className="section-wrap">
          <div className="empty-state">
            <span className="eyebrow">Ejendom</span>
            <h1>Ejendommen blev ikke fundet</h1>
            <p>Vi kunne ikke hente oplysninger om den ønskede ejendom lige nu.</p>
            <div className="empty-state__actions">
              <Link className="site-btn site-btn--primary" to="/ejendomme">
                Tilbage til ejendomme
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const openViewer = (index: number) => {
    setSelectedImageIndex(index);
    onOpen();
  };

  const heroImage = `${strapiUrl}${data.Billede.url}`;

  return (
    <>
      <div className="section-wrap">
        <Link className="detail-back-link fade-in" to="/ejendomme">
          ← Tilbage til oversigten
        </Link>

        <section className="detail-hero fade-in">
          <Image alt={`Hovedbillede af ${data.Adresse}`} src={heroImage} />
          <div className="detail-overlay">
            <div className="detail-overlay__copy">
              <span className="eyebrow" style={{ color: 'rgba(255, 249, 244, 0.82)' }}>
                Ejendom i Silkeborg
              </span>
              <h1 className="detail-title">{data.Adresse}</h1>
            </div>
          </div>
        </section>
      </div>

      <section>
        <div className="section-wrap">
          <div className="detail-grid">
            <article className="detail-body fade-in">
              <span className="eyebrow">Om ejendommen</span>
              <Text color="var(--muted)" lineHeight="1.9" mt={5}>
                {data.Beskrivelsse || 'Beskrivelse ikke tilgængelig.'}
              </Text>

              {galleryImages.length > 0 ? (
                <Box mt={10}>
                  <span className="eyebrow">Galleri</span>
                  <Heading as="h3" className="section-title" size="md">
                    Flere billeder
                  </Heading>

                  <div className="gallery-grid" style={{ marginTop: '1.5rem' }}>
                    {galleryImages.map((image, index) => (
                      <article className="gallery-card" key={`${image.url}-${index}`}>
                        <button onClick={() => openViewer(index)} type="button">
                          <img
                            alt={`Galleri billede ${index + 1} af ${data.Adresse}`}
                            loading="lazy"
                            src={`${strapiUrl}${image.url}`}
                          />
                        </button>
                      </article>
                    ))}
                  </div>
                </Box>
              ) : null}
            </article>

            <aside className="detail-sidebar fade-in">
              <div className="detail-sidebar__item">
                <span className="detail-sidebar__label">Adresse</span>
                <div className="detail-sidebar__value">{data.Adresse}</div>
              </div>

              <div className="detail-sidebar__item">
                <span className="detail-sidebar__label">Antal lejemål</span>
                <div className="detail-sidebar__value">{data.Antal_Lejemaal}</div>
              </div>

              {data.Kvadratmeter ? (
                <div className="detail-sidebar__item">
                  <span className="detail-sidebar__label">Samlet areal</span>
                  <div className="detail-sidebar__value">{data.Kvadratmeter} m²</div>
                </div>
              ) : null}
            </aside>
          </div>
        </div>
      </section>

      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay bg="blackAlpha.900" />
        <ModalContent bg="transparent" m={0} maxW="100vw">
          <ModalCloseButton color="white" size="lg" />
          <ModalBody display="flex" minH="100vh" alignItems="center" justifyContent="center" p={8}>
            {galleryImages[selectedImageIndex] ? (
              <Box maxW="90vw">
                <Image
                  alt={`Billede ${selectedImageIndex + 1} af ${data.Adresse}`}
                  borderRadius="lg"
                  boxShadow="2xl"
                  maxH="82vh"
                  objectFit="contain"
                  src={`${strapiUrl}${galleryImages[selectedImageIndex].url}`}
                />
                <p className="modal-caption">
                  {selectedImageIndex + 1} af {galleryImages.length}
                </p>
              </Box>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
});

Ejendom.displayName = 'Ejendom';

export default Ejendom;
